import { computed, effect, inject, Injectable, signal } from '@angular/core';
import { FormatMatchResult } from '../interfaces/format-match-result.interface';
import { buildProvenanceIndex, EInvoiceFieldSelection } from '../utils/provenance-index';
import { buildSchemaLabelIndex, EMPTY_SCHEMA_LABEL_INDEX } from '../utils/schema-label-index';
import { InvoiceService } from './invoice.service';

export interface EInvoiceViewerDocumentContext {
  model: any | undefined;
  format?: FormatMatchResult;
  rawXml?: string;
  xmlFileName?: string;
}

@Injectable()
export class EinvoiceViewerContextService {
  private readonly invoiceService = inject(InvoiceService);
  private readonly documentContextSignal = signal<EInvoiceViewerDocumentContext>({ model: undefined });
  private readonly selectedModelPathsSignal = signal<ReadonlySet<string>>(new Set<string>());
  private readonly activeModelPathSignal = signal<string | undefined>(undefined);
  private readonly schemaLabelsSignal = signal(EMPTY_SCHEMA_LABEL_INDEX);
  private schemaLoadSequence = 0;

  readonly documentContext = this.documentContextSignal.asReadonly();
  readonly model = computed(() => this.documentContext().model);
  readonly format = computed(() => this.documentContext().format);
  readonly rawXml = computed(() => this.documentContext().rawXml);
  readonly xmlFileName = computed(() => this.documentContext().xmlFileName);
  readonly provenanceIndex = computed(() => buildProvenanceIndex(this.model()));
  readonly schemaLabels = this.schemaLabelsSignal.asReadonly();
  readonly selectedModelPaths = this.selectedModelPathsSignal.asReadonly();
  readonly activeModelPath = this.activeModelPathSignal.asReadonly();
  readonly selectedEntries = computed(() => {
    const index = this.provenanceIndex();
    return Array.from(this.selectedModelPaths())
      .map(modelPath => index.byModelPath.get(modelPath))
      .filter((entry): entry is EInvoiceFieldSelection => entry !== undefined);
  });

  constructor() {
    effect(() => {
      void this.loadSchemaLabels(this.format());
    });
  }

  setDocumentContext(context: EInvoiceViewerDocumentContext): void {
    this.documentContextSignal.set(context);
    this.clearSelection();
  }

  select(entry: EInvoiceFieldSelection, additive = false): void {
    if (!entry.modelPath) {
      return;
    }

    this.selectedModelPathsSignal.update(current => {
      if (!additive) {
        return new Set<string>([entry.modelPath]);
      }

      const next = new Set<string>(current);
      if (next.has(entry.modelPath)) {
        next.delete(entry.modelPath);
      } else {
        next.add(entry.modelPath);
      }

      return next;
    });
    this.activeModelPathSignal.set(entry.modelPath);
  }

  setSelectedModelPaths(modelPaths: readonly string[], activeModelPath?: string): void {
    this.selectedModelPathsSignal.set(new Set<string>(modelPaths));
    this.activeModelPathSignal.set(activeModelPath ?? modelPaths[modelPaths.length - 1]);
  }

  focusModelPath(modelPath: string | undefined): void {
    this.activeModelPathSignal.set(modelPath);
  }

  clearSelection(): void {
    this.selectedModelPathsSignal.set(new Set<string>());
    this.activeModelPathSignal.set(undefined);
  }

  isSelected(modelPath: string | undefined): boolean {
    return modelPath !== undefined && this.selectedModelPaths().has(modelPath);
  }

  isActive(modelPath: string | undefined): boolean {
    return modelPath !== undefined && this.activeModelPath() === modelPath;
  }

  private async loadSchemaLabels(format: FormatMatchResult | undefined): Promise<void> {
    const loadSequence = ++this.schemaLoadSequence;
    if (!format) {
      this.schemaLabelsSignal.set(EMPTY_SCHEMA_LABEL_INDEX);
      return;
    }

    try {
      const schema = await this.invoiceService.getModelSchema(format);
      if (loadSequence !== this.schemaLoadSequence) {
        return;
      }

      this.schemaLabelsSignal.set(schema ? buildSchemaLabelIndex(schema) : EMPTY_SCHEMA_LABEL_INDEX);
    } catch (error) {
      if (loadSequence !== this.schemaLoadSequence) {
        return;
      }

      console.warn('Could not load e-invoice model schema labels.', error);
      this.schemaLabelsSignal.set(EMPTY_SCHEMA_LABEL_INDEX);
    }
  }
}